import type { PasswordSettings } from 'src/pages/user/account/types';

// ----------------------------------------------------------------------

export const GUEST_BOOT_DATA_ELEMENT_ID = 'guest-boot-data';

export type GuestSocialProviderId = 'kratos' | 'facebook' | 'google' | 'twitter';

export type GuestSocialProvider = {
  id: GuestSocialProviderId;
  label: string;
  href: string;
};

export type GuestCaptcha = {
  enabled: boolean;
  siteKey: string;
};

type SignInBootData = {
  page: 'signIn';
  userEmail: string;
  domainInvitationId: number;
  returnTo: string;
  registrationDisabled: boolean;
  socialProviders: GuestSocialProvider[];
  captcha: GuestCaptcha;
};

type SignUpBootData = {
  page: 'signUp';
  userEmail: string;
  userName: string;
  userDomain: string;
  domainInvitationId: number;
  passwordSettings: PasswordSettings;
  socialProviders: GuestSocialProvider[];
};

type ResetPasswordBootData = {
  page: 'resetPassword';
  requestExpired: boolean;
};

type ResetPasswordTokenBootData = {
  page: 'resetPasswordToken';
  token: string;
  userName: string;
  passwordSettings: PasswordSettings;
};

type UpdatePasswordBootData = {
  page: 'updatePassword';
  passwordExpired: boolean;
  returnTo: string;
  passwordSettings: PasswordSettings;
};

export type GuestBootData =
  | SignInBootData
  | SignUpBootData
  | ResetPasswordBootData
  | ResetPasswordTokenBootData
  | UpdatePasswordBootData;

export type GuestPageId = GuestBootData['page'];

export type GuestBootDataOf<Page extends GuestPageId> = Extract<GuestBootData, { page: Page }>;

// ----------------------------------------------------------------------

let cachedBootData: GuestBootData | null = null;

export function readGuestBootData(): GuestBootData {
  if (cachedBootData) {
    return cachedBootData;
  }

  const element = document.getElementById(GUEST_BOOT_DATA_ELEMENT_ID);
  if (!element?.textContent) {
    throw new Error(`Missing #${GUEST_BOOT_DATA_ELEMENT_ID} boot payload`);
  }

  cachedBootData = JSON.parse(element.textContent) as GuestBootData;
  return cachedBootData;
}

export function useGuestBootData<Page extends GuestPageId>(page: Page): GuestBootDataOf<Page> {
  const bootData = readGuestBootData();

  if (bootData.page !== page) {
    throw new Error(`Guest page "${page}" rendered with boot data for "${bootData.page}"`);
  }

  return bootData as GuestBootDataOf<Page>;
}
